import * as CryptoJS from 'crypto-js';
import { produce } from 'immer';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { getSalt, loginApi } from '@/apis/auth';
import { clientOption } from '@/apis/option';
import { userProfileApi } from '@/apis/user';
import type { AlertTypes } from '@/chat-types/common';
import type { UserInfoResponse } from '@/chat-types/model/user/UserInfoResponse';
import { KEY } from '@/consts/keys';
import { REG_EXP } from '@/utils/checkRegExpRule';
import { getCustomKeyGenerator } from '@/utils/getCustomKeyGenerator';
import { getEncrypt } from '@/utils/getEncrypt';

type LoginState = {
  id: string;
  password: string;
  isShowPassword: boolean;
  isLoading: boolean;
  changePassword: (password: string) => void;
  changeId: (id: string) => void;
  toggleIsShowPassword: () => void;
  loginStatus: {
    type: AlertTypes;
    failCount: number;
    retryCountDown: number;
  };
  resetLoginStatus: () => void;
  login: () => Promise<UserInfoResponse | undefined>;
};

const useLogin = create<LoginState>()(
  devtools(
    (set, get) => ({
      id: '',
      password: '',
      isLoading: false,
      loginStatus: {
        type: 'NONE',
        failCount: 0,
        retryCountDown: 0,
      },
      changePassword(password) {
        set(
          produce((draft: LoginState) => {
            draft.password = password;
            draft.loginStatus.type = 'NONE';
          }),
        );
      },
      changeId(id) {
        set(
          produce((draft: LoginState) => {
            draft.id = id;
            draft.loginStatus.type = 'NONE';
          }),
        );
      },
      isShowPassword: false,
      toggleIsShowPassword() {
        set((state) => ({
          ...state,
          isShowPassword: !state.isShowPassword,
        }));
      },
      async login() {
        let errorText: AlertTypes = 'NONE';

        if (get().id === '') {
          errorText = 'INSERT_EMAIL_HP';
        } else if (get().password === '') {
          errorText = 'INSERT_PASSWORD';
        } else if (!REG_EXP.PASSWORD.test(get().password)) {
          errorText = 'FAILED_PASSWORD_RULE';
        }

        if (errorText !== 'NONE') {
          set(
            produce((draft: LoginState) => {
              draft.loginStatus.type = errorText;
            }),
          );

          return;
        }

        set(
          produce((draft: LoginState) => {
            draft.isLoading = true;
          }),
        );

        try {
          const saltResult = await getSalt();
          const salt = String(saltResult.data.serverTime);

          const encryptionKey = import.meta.env.VITE_APP_ENCRYPTION_KEY;
          const encryptionIv = import.meta.env.VITE_APP_ENCRYPTION_IV;
          const customKey = getCustomKeyGenerator(encryptionKey ?? '', salt);
          const options = {
            encryptKey: customKey,
            encryptIv: encryptionIv,
          };

          const info = { id: get().id, pwd: get().password };

          const infoEncrypt = getEncrypt(JSON.stringify(info), options);

          const formData = new FormData();
          formData.set('salt', salt);
          formData.set('info', infoEncrypt);

          const response = await loginApi(formData);
          const { result, body } = response.data;
          if (result === '20000') {
            const clientResponse = await clientOption();

            localStorage.setItem(
              KEY.API_ID,
              CryptoJS.AES.encrypt(clientResponse.data?.body?.chatKey, clientResponse.data?.body?.clientId).toString(),
            );
            localStorage.setItem(KEY.CLIENT_ID, clientResponse.data?.body?.clientId);

            const userInfoRes = await userProfileApi();

            const { result: userResult, body } = userInfoRes.data;
            if (userResult === '20000') {
              const userInfo = body;
              set(
                produce((draft: LoginState) => {
                  draft.isLoading = false;
                }),
              );

              return userInfo;
            }
          } else if (result === '52001') {
            set(
              produce((draft: LoginState) => {
                draft.isLoading = false;
                draft.loginStatus.type = 'ALL_INCONSISTENCY';
              }),
            );
          } else if (result === '50004') {
            set(
              produce((draft: LoginState) => {
                draft.isLoading = false;
                draft.loginStatus.type = 'FAIL_COUNT';
                draft.loginStatus.failCount = body?.loginFailCnt ?? 0;
              }),
            );
          } else if (result === '50104') {
            set(
              produce((draft: LoginState) => {
                draft.isLoading = false;
                draft.loginStatus.type = 'LOGIN_LOCKED';
                draft.loginStatus.retryCountDown = body?.lockoutTimeRemaining ?? 1000 * 60 * 5;
              }),
            );
          } else if (result === 'J0004') {
            set(
              produce((draft: LoginState) => {
                draft.isLoading = false;
                draft.loginStatus.type = 'DEACTIVATED';
              }),
            );
          } else {
            set(
              produce((draft: LoginState) => {
                draft.isLoading = false;
              }),
            );
            throw new Error('server error');
          }
          return;
        } catch (e) {
          set(
            produce((draft: LoginState) => {
              draft.isLoading = false;
            }),
          );

          throw new Error('server error');
        }
      },
      resetLoginStatus() {
        set(
          produce((draft: LoginState) => {
            draft.loginStatus.type = 'NONE';
            draft.loginStatus.retryCountDown = 0;
            draft.loginStatus.failCount = 0;
          }),
        );
      },
    }),
    { name: 'LOGIN_KEY' },
  ),
);

export default useLogin;
